import { LoadingOverlay } from '@mantine/core';
import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import {
  AuthorDetailPage,
  AuthorsPage,
  AuthPage,
  BookDescription,
  BookDetailPage,
  BookLessonDetailPage,
  BookLessonsBatchDetailPage,
  BooksPage,
  CategoriesDetailPage,
  CategoriesPage,
  CreateAuthorPage,
  CreateCategoryPage,
  CreatePublisherPage,
  EditAuthorPage,
  EditCategoryPage,
  EditPublisherPage,
  PublisherDetailPage,
  PublishersPage,
} from '@/pages';
import { isProject } from '@/shared/config/projects';
import { RouteList } from '@/shared/constants';
import { legacyProjectPath, projectPath } from '@/shared/projects/project-path';
import { useGlobalStore } from '@/shared/stores/global.store';
import { DashboardLayout } from '@/shared/ui';

export const AppRouter = () => {
  const location = useLocation();
  const activeProject = useGlobalStore((state) => state.activeProject);
  const setActiveProject = useGlobalStore((state) => state.setActiveProject);
  const checkAuthorized = useGlobalStore((state) => state.checkAuthorized);
  const routeSegment = location.pathname.split('/')[1];
  const hasProjectPrefix = isProject(routeSegment);
  const project = hasProjectPrefix ? routeSegment : activeProject;
  const isAuthorized = useGlobalStore(
    (state) => state.sessions[project].isAuthorized,
  );

  useEffect(() => {
    if (hasProjectPrefix) {
      setActiveProject(project);
      checkAuthorized(project);
    }
  }, [checkAuthorized, hasProjectPrefix, project, setActiveProject]);

  if (!hasProjectPrefix) {
    const target = legacyProjectPath(activeProject, location.pathname);
    return <Navigate to={`${target}${location.search}`} replace />;
  }

  if (isAuthorized === undefined) {
    return <LoadingOverlay visible />;
  }

  if (!isAuthorized) {
    return (
      <Routes>
        <Route
          path={projectPath(project, RouteList.AUTH)}
          element={<AuthPage />}
        />
        <Route
          path="*"
          element={
            <Navigate to={projectPath(project, RouteList.AUTH)} replace />
          }
        />
      </Routes>
    );
  }

  return (
    <DashboardLayout>
      <Routes>
        <Route
          path={projectPath(project, RouteList.HOME)}
          element={
            <Navigate to={projectPath(project, RouteList.BOOKS)} replace />
          }
        />
        <Route
          path={projectPath(project, RouteList.BOOKS)}
          element={<BooksPage />}
        />
        <Route
          path={projectPath(project, RouteList.BOOK_DETAILS(':bookId'))}
          element={<BookDetailPage />}
        />
        <Route
          path={projectPath(
            project,
            RouteList.BOOK_DETAILS(':bookId/description'),
          )}
          element={<BookDescription />}
        />
        {project === 'hatfa' && (
          <>
            <Route
              path={projectPath(
                project,
                RouteList.LESSON_DETAILS(':bookId', ':lessonId'),
              )}
              element={<BookLessonDetailPage />}
            />
            <Route
              path={projectPath(
                project,
                RouteList.BATCH_LESSON_UPLOAD(':bookId'),
              )}
              element={<BookLessonsBatchDetailPage />}
            />
          </>
        )}
        <Route
          path={projectPath(project, RouteList.CATEGORIES)}
          element={<CategoriesPage />}
        />
        <Route
          path={projectPath(project, RouteList.EDIT_CATEGORY(':categoryId'))}
          element={<EditCategoryPage />}
        />
        <Route
          path={projectPath(project, RouteList.CREATE_CATEGORY)}
          element={<CreateCategoryPage />}
        />
        <Route
          path={projectPath(project, RouteList.CATEGORY_DETAILS(':categoryId'))}
          element={<CategoriesDetailPage />}
        />
        <Route
          path={projectPath(project, RouteList.AUTHORS)}
          element={<AuthorsPage />}
        />
        <Route
          path={projectPath(project, RouteList.EDIT_AUTHOR(':authorId'))}
          element={<EditAuthorPage />}
        />
        <Route
          path={projectPath(project, RouteList.CREATE_AUTHOR)}
          element={<CreateAuthorPage />}
        />
        <Route
          path={projectPath(project, RouteList.AUTHOR_DETAILS(':authorId'))}
          element={<AuthorDetailPage />}
        />
        <Route
          path={projectPath(project, RouteList.PUBLISHERS)}
          element={<PublishersPage />}
        />
        <Route
          path={projectPath(
            project,
            RouteList.PUBLISHER_DETAILS(':publisherId'),
          )}
          element={<PublisherDetailPage />}
        />
        <Route
          path={projectPath(project, RouteList.EDIT_PUBLISHER(':publisherId'))}
          element={<EditPublisherPage />}
        />
        <Route
          path={projectPath(project, RouteList.CREATE_PUBLISHER)}
          element={<CreatePublisherPage />}
        />
        <Route
          path="*"
          element={
            <Navigate to={projectPath(project, RouteList.BOOKS)} replace />
          }
        />
      </Routes>
    </DashboardLayout>
  );
};
